import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, Validators, FormArray } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { MiscTools } from 'appshared/misc-tools';
import { TextTools } from 'appshared/text-tools';
import { UITools } from 'appshared/ui-tools';

import { ReportsService } from '../reports.service';
import { AuthService } from 'client/app/services/auth.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';
import { OrganizationsService } from '../../organizations/organizations.service';
import { DashboardService } from '../../dashboard/dashboard.service';

@Component({
	selector: 'app-reports-list',
	templateUrl: './reports-list.component.html',
	styleUrls: ['./reports-list.component.scss']
})
export class ReportsListComponent implements OnInit, OnDestroy {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;
	ut = UITools;

	private userSubscription: Subscription;

	orgSelections: any[] = [];
	accountOwnerSelections: any[] = [];
	yesNo = ['Yes', 'No'];

	// ***************************************************************
	// variables for BxVersionReport
	bxVersionReportForm: UntypedFormGroup;
	bxVersionReportKeyTypes: string[] = ['production'];

	// ***************************************************************
	// variables for CommercialAnalysisReport
	commercialAnalysisReportForm: UntypedFormGroup;
	commercialAnalysisReportStart: NgbDateStruct = null;
	commercialAnalysisReportEnd: NgbDateStruct = null;
	commercialAnalysisReportOrgIds: number[] = [];

	// ***************************************************************
	// variables for TranscodeReport
	transcodeReportForm: UntypedFormGroup;
	transcodeReportStart: NgbDateStruct = null;
	transcodeReportEnd: NgbDateStruct = null;

	// ***************************************************************
	// variables for CommercialAnalysisReport
	systemWideUsageReportForm: UntypedFormGroup;
	systemWideUsageReportStart: NgbDateStruct = null;
	systemWideUsageReportEnd: NgbDateStruct = null;
	systemWideReportOrgIds: number[] = [];
	systemWideReportByHostId: boolean = false;
	systemWideReportByBillingCode: boolean = false;

	systemWideUsageReportKeyProduct: string = 'broadcaster*';
	systemWideUsageReportKeyProductSelections: any[] = [
		{ value: 'broadcaster*', label: 'Broadcaster Keys' },
		{ value: 'broadcaster_zenm_generic', label: 'ZEN Master Generic Usage Keys' },
		{ value: 'broadcaster_zenm_aws_mx', label: 'ZEN Master MediaConnect Usage Keys' },
	];

	// ***************************************************************
	// variables for UsagePatternsReport
	usagePatternsReportForm: UntypedFormGroup;
	usagePatternsReportBlockDays: number = -30;
	usagePatternsReportSliceDays: number = 7;
	usagePatternsReportNumSlices: number = 4;
	usagePatternsReportKeyTypes: string[] = ['production'];
	usagePatternsReportDataSource: string = 'billable';

	usagePatternsReportBlocks: any[] = [
		{
			value: -30,
			label: 'Monthly'
		}, {
			value: -90,
			label: 'Quarterly'
		}, {
			value: -365,
			label: 'Yearly'
		}
	];

	usagePatternsReportDataSourceSelections: any[] = [
		{
			value: 'billable',
			label: 'Billable Usage Based on Commerial Type',
			information: ''
		},
		{
			value: 'in_out',
			label: 'All Ingress & Egress Traffic',
			information: ''
		},
		{
			value: 'in',
			label: 'All Ingress Traffic',
			information: ''
		},
		{
			value: 'out',
			label: 'All Egress Traffic',
			information: ''
		},
		{
			value: 'transcode',
			label: 'All Recorded Transcode Traffic',
			information: ''
		},
		{
			value: 'protected_mb',
			label: 'Protected Meter Traffic',
			information: ''
		},
		{
			value: 'output_mb_meter',
			label: 'All Outputs Meter Traffic',
			information: ''
		}
	];

	// ***************************************************************
	// variables for IncidentsReport
	incidentsReportForm: UntypedFormGroup;
	incidentsReportStart: NgbDateStruct = null;
	incidentsReportEnd: NgbDateStruct = null;

	// variables for AccountReviewReport
	accountReviewReportForm: UntypedFormGroup;
	accountReviewReportStart: NgbDateStruct = null;
	accountReviewReportEnd: NgbDateStruct = null;

	quarterTimeFrames: any[] = [];
	monthTimeFrames: any[] = [];

	topCache: Models.TopUsageCacheEntry[] = [];

	// - key type - def Production
	// - filter by account owner(s)/sales engineer(s)
	// - data to use

	constructor(
		private authService: AuthService,
		private uiAlertsService: UiAlertsService,
		private organizationsService: OrganizationsService,
		private dashboardService: DashboardService,
		private reportsService: ReportsService) { }

	// ***************************************************************
	ngOnInit(): void {
		this.userSubscription = this.authService.user.subscribe((authUser) => {
			this.setup();

			// this.canAdd = authUser && ValidationTools.checkAccess(authUser, 'manage-files');
		});
	}

	// ***************************************************************
	ngOnDestroy() {
		if (this.userSubscription) this.userSubscription.unsubscribe();
	}

	// ***************************************************************
	async setup() {
		const allOrgs: Models.Organization[] = this.organizationsService.getAll();
		this.orgSelections = [];
		this.accountOwnerSelections = [];

		for (const org of allOrgs) {
			this.orgSelections.push({
				value: org.id,
				label: org.name + ' [' + org.otype + '] - ' + org.salesforce_account_owner
			});

			if (org.salesforce_account_owner_id !== 0 && org.salesforce_account_owner && org.salesforce_account_owner !== '') {
				if (MiscTools.findIndexGeneric(this.accountOwnerSelections, 'value', org.salesforce_account_owner_id) === -1) {
					this.accountOwnerSelections.push({
						value: org.salesforce_account_owner_id,
						label: org.salesforce_account_owner
					});
				} // if
			} // if
		} // for

		this.accountOwnerSelections.sort((a, b) => (a.label > b.label) ? 1 : -1);

		// ***************************************************************
		// set-up for the UsagePatternsReport
		// pre-load start / end with the last 30 days...
		this.bxVersionReportForm = new UntypedFormGroup({
			keyTypes: new UntypedFormControl(this.bxVersionReportKeyTypes, [Validators.required]),
		});

		// ***************************************************************
		// set-up for the CommercialAnalysisReport
		// pre-load start / end with the last 30 days...
		const endCAR = new Date();
		const startCAR = MiscTools.dateBackFromNow(30);

		this.commercialAnalysisReportStart = { day: startCAR.getDate(), month: startCAR.getMonth() + 1, year: startCAR.getFullYear() };
		this.commercialAnalysisReportEnd = { day: endCAR.getDate(), month: endCAR.getMonth() + 1, year: endCAR.getFullYear() };
		this.commercialAnalysisReportForm = new UntypedFormGroup({
			startDate: new UntypedFormControl(this.commercialAnalysisReportStart),
			endDate: new UntypedFormControl(this.commercialAnalysisReportEnd),
			orgIds: new UntypedFormControl(this.commercialAnalysisReportOrgIds)
		});

		// ***************************************************************
		// set-up for the CommercialAnalysisReport
		// pre-load start / end with the last 30 days...
		const endSWR = new Date();
		const startSWR = MiscTools.dateBackFromNow(30);

		this.systemWideUsageReportStart = { day: startSWR.getDate(), month: startSWR.getMonth() + 1, year: startSWR.getFullYear() };
		this.systemWideUsageReportEnd = { day: endSWR.getDate(), month: endSWR.getMonth() + 1, year: endSWR.getFullYear() };
		this.systemWideUsageReportForm = new UntypedFormGroup({
			startDate: new UntypedFormControl(this.systemWideUsageReportStart),
			endDate: new UntypedFormControl(this.systemWideUsageReportEnd),
			keyProduct: new UntypedFormControl(this.systemWideUsageReportKeyProduct),
			orgIds: new UntypedFormControl(this.systemWideReportOrgIds),
			systemWideReportByHostId: new UntypedFormControl(this.systemWideReportByHostId ? 'Yes' : 'No'),
			systemWideReportByBillingCode: new UntypedFormControl(this.systemWideReportByBillingCode ? 'Yes' : 'No'),
		});

		// ***************************************************************
		// set-up for the TranscodeReport
		// pre-load start / end with the last 365 days...
		const endH2R = new Date();
		const startH2R = MiscTools.dateBackFromNow(365);

		this.transcodeReportStart = { day: startH2R.getDate(), month: startH2R.getMonth() + 1, year: startH2R.getFullYear() };
		this.transcodeReportEnd = { day: endH2R.getDate(), month: endH2R.getMonth() + 1, year: endH2R.getFullYear() };
		this.transcodeReportForm = new UntypedFormGroup({
			startDate: new UntypedFormControl(this.transcodeReportStart),
			endDate: new UntypedFormControl(this.transcodeReportEnd)
		});

		// ***************************************************************
		// set-up for the UsagePatternsReport
		// pre-load start / end with the last 30 days...
		this.usagePatternsReportForm = new UntypedFormGroup({
			sliceMode: new UntypedFormControl('days'),
			sliceDaysAlt: new UntypedFormControl(this.usagePatternsReportBlockDays),
			sliceDays: new UntypedFormControl(this.usagePatternsReportSliceDays),
			numSlices: new UntypedFormControl(this.usagePatternsReportNumSlices, [Validators.required]),
			keyTypes: new UntypedFormControl(this.usagePatternsReportKeyTypes, [Validators.required]),
			dataSource: new UntypedFormControl(this.usagePatternsReportDataSource, [Validators.required]),
			orgIds: new UntypedFormControl(null, []),
		});

		// ***************************************************************
		// set-up for the IncidentsReport
		// pre-load start / end with the last 30 days...
		this.incidentsReportStart = { day: startH2R.getDate(), month: startH2R.getMonth() + 1, year: startH2R.getFullYear() };
		this.incidentsReportEnd = { day: endH2R.getDate(), month: endH2R.getMonth() + 1, year: endH2R.getFullYear() };
		this.incidentsReportForm = new UntypedFormGroup({
			startDate: new UntypedFormControl(this.incidentsReportStart),
			endDate: new UntypedFormControl(this.incidentsReportEnd)
		});

		// ***************************************************************
		// set-up for the AccountReviewReport
		// pre-load start / end with the last 30 days...
		this.accountReviewReportStart = { day: startH2R.getUTCDate(), month: startH2R.getUTCMonth() + 1, year: startH2R.getUTCFullYear() };
		this.accountReviewReportEnd = { day: endH2R.getUTCDate(), month: endH2R.getUTCMonth() + 1, year: endH2R.getUTCFullYear() };


		//		this.accountReviewTimeFrames.push();
		/*
			for options.....
				skip disabled organziations...

				skip orgs with no enable Bx Keys and no enabled ZEN Sites...

				do pre build date blocks
					this + last 4 quarters
					this month (when no first day of month)
					last month
					this year (when not first day of year)
					last year
			*/

		this.quarterTimeFrames = [];
		this.monthTimeFrames = [];

		const now: Date = new Date();
		const backOneYear: Date = MiscTools.dateIntervalAdd(now, -12, 'months');
		backOneYear.setUTCDate(1);
		backOneYear.setUTCHours(0);
		backOneYear.setUTCMinutes(0);
		backOneYear.setUTCSeconds(0);
		backOneYear.setUTCMilliseconds(0);

		const backFiveQuarters: Date = MiscTools.dateIntervalAdd(now, -15, 'months');
		backFiveQuarters.setUTCDate(1);
		backFiveQuarters.setUTCHours(0);
		backFiveQuarters.setUTCMinutes(0);
		backFiveQuarters.setUTCSeconds(0);
		backFiveQuarters.setUTCMilliseconds(0);

		let quarterCounter: Date = new Date(backFiveQuarters);
		while (quarterCounter.getTime() < now.getTime()) {
			if ([0, 3, 6, 9].includes(quarterCounter.getUTCMonth())) { // January
				let label: string = 'Q';
				if (quarterCounter.getUTCMonth() === 0)
					label += '1';
				else if (quarterCounter.getUTCMonth() === 3)
					label += '2';
				else if (quarterCounter.getUTCMonth() === 6)
					label += '3';
				else if (quarterCounter.getUTCMonth() === 9)
					label += '4';

				label += ' ' + quarterCounter.getUTCFullYear();

				this.quarterTimeFrames.push({
					from: new Date(quarterCounter),
					to: MiscTools.dateIntervalAdd(quarterCounter, 3, 'month'),
					label: label
				});
			} // if
			quarterCounter = MiscTools.dateIntervalAdd(quarterCounter, 1, 'month');
		} // while

		let monthCounter: Date = new Date(backOneYear);
		while (monthCounter.getTime() < now.getTime()) {
			let label: string = AppConstants.months[monthCounter.getUTCMonth()].substring(0, 3) + '.'
				+ ' ' + monthCounter.getUTCFullYear();

			this.monthTimeFrames.push({
				from: new Date(monthCounter),
				to: MiscTools.dateIntervalAdd(monthCounter, 1, 'month'),
				label: label
			});
			monthCounter = MiscTools.dateIntervalAdd(monthCounter, 1, 'month');
		} // while

		this.accountReviewReportForm = new UntypedFormGroup({
			skipDisabledOrgs: new UntypedFormControl('Yes'),
			skipUniqueProtocols: new UntypedFormControl('Yes'),
			accountOwnerId: new UntypedFormControl(null),
			orgIds: new UntypedFormControl(null, []),
			startDate: new UntypedFormControl(this.accountReviewReportStart),
			endDate: new UntypedFormControl(this.accountReviewReportEnd)
		});


		this.topCache = await this.dashboardService.getTopUsageCache('last-30', ['production'], 'organization');

		this.topCache.sort((a, b) => (+a.used < +b.used) ? 1 : -1);

	}

	// ***************************************************************
	async runReport(className: string) {
		await this.reportsService.runReport(className, '');
	}

	// ***************************************************************
	async runCommercialAnalysisReport() {
		this.commercialAnalysisReportStart = this.commercialAnalysisReportForm.value.startDate;
		this.commercialAnalysisReportEnd = this.commercialAnalysisReportForm.value.endDate;
		this.commercialAnalysisReportOrgIds = this.commercialAnalysisReportForm.value.orgIds ? this.commercialAnalysisReportForm.value.orgIds : [];

		let startDate: Date = null;
		if (this.commercialAnalysisReportStart) {
			startDate = new Date(this.commercialAnalysisReportStart.year + '/' + this.commercialAnalysisReportStart.month + '/' + this.commercialAnalysisReportStart.day);
			if (isNaN(startDate.getTime())) startDate = null;
		}

		let endDate: Date = null;
		if (this.commercialAnalysisReportEnd) {
			endDate = new Date(this.commercialAnalysisReportEnd.year + '/' + this.commercialAnalysisReportEnd.month + '/' + this.commercialAnalysisReportEnd.day);
			if (isNaN(endDate.getTime())) endDate = null;
		}

		if (startDate != null && endDate != null) {
			const args = 'start=' + TextTools.formatDateUTC(startDate)
				+ '&end=' + TextTools.formatDateUTC(endDate)
				+ '&orgIds=' + (this.commercialAnalysisReportOrgIds ? this.commercialAnalysisReportOrgIds.join(',') : '');

			if (document.getElementById("closeCommercialAnalysisReportModal"))
				document.getElementById("closeCommercialAnalysisReportModal").click();

			await this.reportsService.runReport('CommercialAnalysisReport', args);

		} else {
			this.uiAlertsService.addMsg('You must supply a valid start and end date.', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		} // if
	}

	// ***************************************************************
	async runSystemWideUsageReport() {
		this.systemWideUsageReportStart = this.systemWideUsageReportForm.value.startDate;
		this.systemWideUsageReportEnd = this.systemWideUsageReportForm.value.endDate;
		this.systemWideUsageReportKeyProduct = this.systemWideUsageReportForm.value.keyProduct;

		this.systemWideReportOrgIds = this.systemWideUsageReportForm.value.orgIds ? this.systemWideUsageReportForm.value.orgIds : [];
		this.systemWideReportByHostId = this.systemWideUsageReportForm.value.systemWideReportByHostId === 'Yes';
		this.systemWideReportByBillingCode = this.systemWideUsageReportForm.value.systemWideReportByBillingCode === 'Yes';

		let startDate: Date = null;
		if (this.systemWideUsageReportStart) {
			startDate = new Date(this.systemWideUsageReportStart.year + '/' + this.systemWideUsageReportStart.month + '/' + this.systemWideUsageReportStart.day);
			if (isNaN(startDate.getTime())) startDate = null;
		}

		let endDate: Date = null;
		if (this.systemWideUsageReportEnd) {
			endDate = new Date(this.systemWideUsageReportEnd.year + '/' + this.systemWideUsageReportEnd.month + '/' + this.systemWideUsageReportEnd.day);
			if (isNaN(endDate.getTime())) endDate = null;
		}

		if ((this.systemWideReportByHostId || this.systemWideReportByBillingCode) && (!this.systemWideReportOrgIds || this.systemWideReportOrgIds.length === 0)) {
			this.uiAlertsService.addMsg('To break down by host ID and/or billing code, you must select an organization.', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			return;
		} // if

		if (this.systemWideReportByHostId && this.systemWideReportByBillingCode) {
			this.uiAlertsService.addMsg('You can break the report down by host ID or billing code, but not both.', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			return;
		} // if

		if (startDate != null && endDate != null && this.systemWideUsageReportKeyProduct && this.systemWideUsageReportKeyProduct !== '') {
			const args = 'start=' + TextTools.formatDateUTC(startDate)
				+ '&end=' + TextTools.formatDateUTC(endDate)
				+ '&keyProduct=' + encodeURIComponent(this.systemWideUsageReportKeyProduct)
				+ '&orgIds=' + (this.systemWideReportOrgIds ? this.systemWideReportOrgIds.join(',') : '')
				+ '&byHostId=' + (this.systemWideReportByHostId ? 'yes' : '')
				+ '&byBillingCode=' + (this.systemWideReportByBillingCode ? 'yes' : '');

			if (document.getElementById("closeSystemWideUsageReportModal"))
				document.getElementById("closeSystemWideUsageReportModal").click();

			await this.reportsService.runReport('SystemWideUsageReport', args);

		} else {
			this.uiAlertsService.addMsg('You must supply a valid start and end date.', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		} // if
	}

	// ***************************************************************
	async runTranscodeReport() {
		this.transcodeReportStart = this.transcodeReportForm.value.startDate;
		this.transcodeReportEnd = this.transcodeReportForm.value.endDate;

		let startDate: Date = null;
		if (this.transcodeReportStart) {
			startDate = new Date(this.transcodeReportStart.year + '/' + this.transcodeReportStart.month + '/' + this.transcodeReportStart.day);
			if (isNaN(startDate.getTime())) startDate = null;
		}

		let endDate: Date = null;
		if (this.transcodeReportEnd) {
			endDate = new Date(this.transcodeReportEnd.year + '/' + this.transcodeReportEnd.month + '/' + this.transcodeReportEnd.day);
			if (isNaN(endDate.getTime())) endDate = null;
		}

		if (startDate != null && endDate != null) {
			const args = 'start=' + TextTools.formatDateUTC(startDate)
				+ '&end=' + TextTools.formatDateUTC(endDate);
			if (document.getElementById("closeTranscodeReportModal"))
				document.getElementById("closeTranscodeReportModal").click();

			await this.reportsService.runReport('TranscodeReport', args);

		} else {
			this.uiAlertsService.addMsg('You must supply a valid start and end date.', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		} // if
	}

	// ***************************************************************
	async runIncidentsReport() {
		this.incidentsReportStart = this.incidentsReportForm.value.startDate;
		this.incidentsReportEnd = this.incidentsReportForm.value.endDate;

		let startDate: Date = null;
		if (this.incidentsReportStart) {
			startDate = new Date(this.incidentsReportStart.year + '/' + this.incidentsReportStart.month + '/' + this.incidentsReportStart.day);
			if (isNaN(startDate.getTime())) startDate = null;
		}

		let endDate: Date = null;
		if (this.incidentsReportEnd) {
			endDate = new Date(this.incidentsReportEnd.year + '/' + this.incidentsReportEnd.month + '/' + this.incidentsReportEnd.day);
			if (isNaN(endDate.getTime())) endDate = null;
		}

		if (startDate != null && endDate != null) {
			const args = 'start=' + TextTools.formatDateUTC(startDate)
				+ '&end=' + TextTools.formatDateUTC(endDate);
			if (document.getElementById("closeIncidentsReportModal"))
				document.getElementById("closeIncidentsReportModal").click();

			await this.reportsService.runReport('IncidentsReport', args);

		} else {
			this.uiAlertsService.addMsg('You must supply a valid start and end date.', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		} // if
	}

	// ***************************************************************
	async runUsagePatternsReport() {
		const sliceMode: string = this.usagePatternsReportForm.value.sliceMode;
		const sliceDays: number = +this.usagePatternsReportForm.value.sliceDays;
		const sliceDaysAlt: number = +this.usagePatternsReportForm.value.sliceDaysAlt;
		const numSlices: number = +this.usagePatternsReportForm.value.numSlices;
		const keyTypes: string[] = this.usagePatternsReportForm.value.keyTypes;
		const dataSource: string = this.usagePatternsReportForm.value.dataSource;
		const orgIds: number[] = this.usagePatternsReportForm.value.orgIds ? this.usagePatternsReportForm.value.orgIds : [];

		let sliceDaysToUse: number = 0;
		if (sliceMode === 'block')
			sliceDaysToUse = sliceDaysAlt;
		else
			sliceDaysToUse = sliceDays;

		const args = 'sliceDays=' + sliceDaysToUse
			+ '&numSlices=' + numSlices
			+ '&keyTypes=' + encodeURIComponent(keyTypes.join(','))
			+ '&dataSource=' + encodeURIComponent(dataSource)
			+ '&orgIds=' + encodeURIComponent(orgIds.join(','))
			;

		if (document.getElementById("closeUsagePatternsReportModal"))
			document.getElementById("closeUsagePatternsReportModal").click();

		await this.reportsService.runReport('UsagePatternsReport', args);
	}

	// ***************************************************************
	async runBxVersionReport() {
		const keyTypes: string[] = this.bxVersionReportForm.value.keyTypes;

		const args = 'keyTypes=' + encodeURIComponent(keyTypes.join(','));

		if (document.getElementById("closeBxVersionReportModal"))
			document.getElementById("closeBxVersionReportModal").click();

		await this.reportsService.runReport('BxVersionReport', args);
	}

	// ***************************************************************
	async setDateForAccountRevew(timeFrame: any) {
		this.accountReviewReportStart = { day: timeFrame.from.getUTCDate(), month: timeFrame.from.getUTCMonth() + 1, year: timeFrame.from.getUTCFullYear() };
		this.accountReviewReportEnd = { day: timeFrame.to.getUTCDate(), month: timeFrame.to.getUTCMonth() + 1, year: timeFrame.to.getUTCFullYear() };
		this.accountReviewReportForm.controls['startDate'].setValue(this.accountReviewReportStart);
		this.accountReviewReportForm.controls['endDate'].setValue(this.accountReviewReportEnd);
	} // if

	// ***************************************************************
	async setDateForCommercial(timeFrame: any) {
		// console.log('setDateForCommercial');
		// console.log(timeFrame);
		this.commercialAnalysisReportStart = { day: timeFrame.from.getUTCDate(), month: timeFrame.from.getUTCMonth() + 1, year: timeFrame.from.getUTCFullYear() };
		this.commercialAnalysisReportEnd = { day: timeFrame.to.getUTCDate(), month: timeFrame.to.getUTCMonth() + 1, year: timeFrame.to.getUTCFullYear() };
		this.commercialAnalysisReportForm.controls['startDate'].setValue(this.commercialAnalysisReportStart);
		this.commercialAnalysisReportForm.controls['endDate'].setValue(this.commercialAnalysisReportEnd);
	} // if

	// ***************************************************************
	async setDateForSystemWide(timeFrame: any) {
		this.systemWideUsageReportStart = { day: timeFrame.from.getUTCDate(), month: timeFrame.from.getUTCMonth() + 1, year: timeFrame.from.getUTCFullYear() };
		this.systemWideUsageReportEnd = { day: timeFrame.to.getUTCDate(), month: timeFrame.to.getUTCMonth() + 1, year: timeFrame.to.getUTCFullYear() };
		this.systemWideUsageReportForm.controls['startDate'].setValue(this.systemWideUsageReportStart);
		this.systemWideUsageReportForm.controls['endDate'].setValue(this.systemWideUsageReportEnd);
	} // if

	// ***************************************************************
	async selectTopOrganizations(numOrgs: number) {
		const topOrgIds = [];
		for (const t of this.topCache)
			if (topOrgIds.length < numOrgs)
				topOrgIds.push(t.object_id);
		this.accountReviewReportForm.controls['orgIds'].setValue(topOrgIds);
	} // if

	// ***************************************************************
	async runAccountReviewReport() {
		this.accountReviewReportStart = this.accountReviewReportForm.value.startDate;
		this.accountReviewReportEnd = this.accountReviewReportForm.value.endDate;

		let startDate: Date = null;
		if (this.accountReviewReportStart) {
			startDate = new Date(this.accountReviewReportStart.year + '/' + this.accountReviewReportStart.month + '/' + this.accountReviewReportStart.day);
			if (isNaN(startDate.getTime())) startDate = null;
		}

		let endDate: Date = null;
		if (this.accountReviewReportEnd) {
			endDate = new Date(this.accountReviewReportEnd.year + '/' + this.accountReviewReportEnd.month + '/' + this.accountReviewReportEnd.day);
			if (isNaN(endDate.getTime())) endDate = null;
		}

		const orgIds: number[] = this.accountReviewReportForm.value.orgIds ? this.accountReviewReportForm.value.orgIds : [];
		let accountOwnerId: number = +this.accountReviewReportForm.value.accountOwnerId;
		if (isNaN(accountOwnerId)) accountOwnerId = 0;

		let options: string = '';
		if (this.accountReviewReportForm.value.skipUniqueProtocols && this.accountReviewReportForm.value.skipUniqueProtocols === 'Yes')
			options += 'skipUniqueProtocols ';

		if (this.accountReviewReportForm.value.skipDisabledOrgs && this.accountReviewReportForm.value.skipDisabledOrgs === 'Yes')
			options += 'skipDisabledOrgs ';

		let errors: string[] = [];
		if (startDate == null)
			errors.push('You must supply a valid start date.');

		if (endDate == null)
			errors.push('You must supply a valid end date.');

		if ((!orgIds || orgIds.length === 0) && accountOwnerId === 0)
			errors.push('You must choose at least one organization or pick an account owner');

		if (orgIds && orgIds.length !== 0 && accountOwnerId !== 0)
			errors.push('You can only run the report on specific organizations or those for an account owner');

		if (errors.length > 0) {
			this.uiAlertsService.addMsgs(errors, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		} else {
			let args: string = 'start=' + TextTools.formatDateUTC(startDate)
				+ '&end=' + TextTools.formatDateUTC(endDate);
			if (orgIds && orgIds.length !== 0)
				args += '&orgIds=' + encodeURIComponent(orgIds.join(','));
			else if (accountOwnerId !== 0)
				args += '&accountOwnerIds=' + accountOwnerId;

			if (options !== '')
				args += '&options=' + encodeURIComponent(options);

			if (document.getElementById("closeAccountReviewReportModal"))
				document.getElementById("closeAccountReviewReportModal").click();

			await this.reportsService.runReport('AccountReviewReport', args);
		} // if
	}

	get sliceMode() { return this.usagePatternsReportForm.get('sliceMode'); }

}
